import type { ReactElement, JSX } from "react";

import { PlainTooltip, Textfield } from "@similarweb/ui-components";

import { AutoCompleteKeywordGroupsSingleTab } from "pro-features/components/AutocompleteKeywords/AutoCompleteKeywordGroupsSingleTab";
import { translate } from "app/filters/i18nFilter";

import type { IRule, IRuleItem, MultiIncludeExcludePopupContentProps } from "./types";

type FilterField = "url" | "keyword";

type RuleItemInput = Pick<IRuleItem, "type" | "value">;

export const createMultiIncludeExcludeFilterFromItems = (items: RuleItemInput[], field: FilterField): string => {
    return JSON.stringify([
        {
            type: "Include",
            items,
            field,
            matchType: "Exact",
        },
    ]);
};

export const createMultiIncludeExcludeFilter = (value: string, type: FilterField): string => {
    return createMultiIncludeExcludeFilterFromItems(
        [
            {
                type,
                value,
            },
        ],
        type,
    );
};

export const ConditionalTooltip = ({
    content,
    condition,
    children,
}: {
    content: string;
    condition: boolean;
    children: ReactElement;
}): JSX.Element => {
    return condition ? (
        <PlainTooltip
            placement="top"
            tooltipContent={content}
        >
            <span>{children}</span>
        </PlainTooltip>
    ) : (
        children
    );
};

export const createNewRow = (rule: Partial<IRule> = {}, withMatchType = true): IRule => {
    return {
        // set default values
        type: "Include",
        items: [],
        ...(withMatchType ? { matchType: "Phrase" } : {}),
        // override with provided values
        ...rule,
    };
};

export const MultiIncludeExcludeWebsitesTextField: MultiIncludeExcludePopupContentProps["children"] = (childProps) => {
    return (
        <Textfield
            ref={(ref) => {
                childProps.ref = ref;
            }}
            onApplyClick={() => {
                childProps.onItemSelected(childProps.ref.inputRef.value, childProps.index);
                childProps.ref.clearValue();
            }}
            onKeyUp={(e) => {
                if (e.key === "Enter") {
                    childProps.onItemSelected(childProps.ref.inputRef.value, childProps.index);
                    childProps.ref.clearValue();
                }
            }}
            ApplyButtonText={"add"}
            iconName="globe"
            placeholder={translate(`boolean.search.chip.filter.website.include.placeholder`)}
            onFocus={() => childProps.setAsActiveRow()}
        />
    );
};

export const MultiIncludeExcludeKeywordsTextField: MultiIncludeExcludePopupContentProps["children"] = (childProps) => {
    return (
        <Textfield
            ref={(ref) => {
                childProps.ref = ref;
            }}
            onApplyClick={() => {
                childProps.onItemSelected(childProps.ref.inputRef.value, childProps.index);
                childProps.ref.clearValue();
            }}
            onKeyUp={(e) => {
                if (e.key === "Enter") {
                    childProps.onItemSelected(childProps.ref.inputRef.value, childProps.index);
                    childProps.ref.clearValue();
                }
            }}
            ApplyButtonText={"add"}
            iconName="keywords"
            placeholder={translate(`multi.include.exclude.keyword.placeholder.keyword`)}
            onFocus={() => childProps.setAsActiveRow()}
        />
    );
};

export const MultiIncludeExcludeKeywordGroupsTextField: MultiIncludeExcludePopupContentProps["children"] = (
    childProps,
) => (
    <AutoCompleteKeywordGroupsSingleTab
        onSelect={(selectedValue: any, e: any) =>
            childProps.onItemSelected(selectedValue ?? e.target.innerText, childProps.index)
        }
        textFieldIconName="keywords"
        selectedValue=""
        autocompleteProps={{
            placeholder: translate("multi.include.exclude.keywords.placeholder.keywords"),
            disabled: childProps.isDisabled,
        }}
        keywordsGroupsToFilterFromAutocomplete={childProps.getItemsByItemType(childProps.rule.items, "keywordList")}
        keywordsToFilterFromAutocomplete={childProps.getItemsByItemType(childProps.rule.items, "keyword")}
        className={childProps.isRowActive ? "autocomplete-open" : ""}
        onAutoCompleteToggle={(isOpen) => isOpen && childProps.setAsActiveRow()}
    />
);
